import React, { useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import Button from '../../../components/ui/Button';

const RevenueChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('12m');
  const [selectedView, setSelectedView] = useState('combined');

  const revenueData = [
    { month: 'Jan 2024', mrr: 145000, arr: 1740000, growth: 8.2 },
    { month: 'Feb 2024', mrr: 156000, arr: 1872000, growth: 7.6 },
    { month: 'Mar 2024', mrr: 168000, arr: 2016000, growth: 7.7 },
    { month: 'Apr 2024', mrr: 182000, arr: 2184000, growth: 8.3 },
    { month: 'May 2024', mrr: 195000, arr: 2340000, growth: 7.1 },
    { month: 'Jun 2024', mrr: 208000, arr: 2496000, growth: 6.7 },
    { month: 'Jul 2024', mrr: 223000, arr: 2676000, growth: 7.2 },
    { month: 'Aug 2024', mrr: 238000, arr: 2856000, growth: 6.7 },
    { month: 'Sep 2024', mrr: 254000, arr: 3048000, growth: 6.7 },
    { month: 'Oct 2024', mrr: 271000, arr: 3252000, growth: 6.7 },
    { month: 'Nov 2024', mrr: 289000, arr: 3468000, growth: 6.6 },
    { month: 'Dec 2024', mrr: 308000, arr: 3696000, growth: 6.6 }
  ];

  const periodOptions = [
    { value: '6m', label: '6 Months' },
    { value: '12m', label: '12 Months' },
    { value: '24m', label: '24 Months' }
  ];

  const viewOptions = [
    { value: 'combined', label: 'Combined View', icon: 'BarChart3' },
    { value: 'mrr', label: 'MRR Only', icon: 'BarChart' },
    { value: 'arr', label: 'ARR Only', icon: 'TrendingUp' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevation-3">
          <p className="text-text-primary font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 mb-1">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-text-secondary text-sm">{entry.dataKey.toUpperCase()}:</span>
              <span className="text-text-primary font-medium">
                ${entry.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 dashboard-fade-in">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 space-y-4 lg:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-1">Revenue Trends</h3>
          <p className="text-text-secondary text-sm">Monthly Recurring Revenue & Annual Run Rate</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-text-secondary text-sm">Period:</span>
            <div className="flex bg-muted rounded-lg p-1">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedPeriod(option.value)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-all duration-150 ${
                    selectedPeriod === option.value
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {viewOptions.map((option) => (
              <Button
                key={option.value}
                variant={selectedView === option.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSelectedView(option.value)}
                iconName={option.icon}
                iconPosition="left"
                iconSize={16}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={revenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => value.split(' ')[0]}
            />
            <YAxis 
              yAxisId="left"
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {(selectedView === 'combined' || selectedView === 'mrr') && (
              <Bar 
                yAxisId="left"
                dataKey="mrr" 
                fill="var(--color-primary)" 
                name="Monthly Recurring Revenue"
                radius={[4, 4, 0, 0]}
              />
            )}
            
            {(selectedView === 'combined' || selectedView === 'arr') && (
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="arr" 
                stroke="var(--color-secondary)" 
                strokeWidth={3}
                name="Annual Run Rate"
                dot={{ fill: 'var(--color-secondary)', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: 'var(--color-secondary)', strokeWidth: 2 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded-full"></div>
            <span className="text-text-secondary text-sm">MRR Growth: +6.6%</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-secondary rounded-full"></div>
            <span className="text-text-secondary text-sm">ARR Growth: +112.4%</span>
          </div>
        </div>
        
        <Button variant="ghost" size="sm" iconName="Download" iconPosition="left">
          Export Data
        </Button>
      </div>
    </div>
  );
};

export default RevenueChart;